import { useContext, useEffect, useState } from "react";
import axios from "axios";
import { AuthContext } from "../../Context/AuthContext";
import { logActivity } from "../../utils/LogActivity";
import Preloader from "../../shared/PreLoaders";
import { Authorization } from "../../Context/Authorization";
import EditLoanRepayment from "./Components/EditLoanRepayment";
import TableComponent from "../../utils/TableComponent";

const inputStyle = {
  width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0",
  borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff",
  outline: "none", transition: "border-color 0.2s", cursor: "pointer",
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "block" };

const formatToLocalTime = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "Invalid Date";
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().split("T")[0];
};

const ManageLoanRepayment = () => {
  const [loanData, setLoanData] = useState([]);
  const [error, setError] = useState(null);
  const [vfcOption, setVfcOption] = useState([]);
  const [selectedVfc, setSelectedVfc] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const { permissions, userId, selectedJfmcType } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [selectedRepaymentId, setSelectedRepaymentId] = useState(null);
  const [editModalShow, setEditModalShow] = useState(false);
  const [loanTypes, setLoanTypes] = useState([]);
  const [selectedLoanType, setSelectedLoanType] = useState("");
  const Api = process.env.REACT_APP_API;

  const fetchVfcNames = async (jfmcTypeId) => {
    setLoading(true);
    try {
      const config = Authorization();
      const response = await axios.post(`${Api}/api/vfcs/names`, { jfmcTypeId }, config);
      const data = response.data.vfcs;
      if (Array.isArray(data)) setVfcOption(data);
      else console.error("API did not return an array", data);
    } catch (error) {
      console.log("Error fetching Data", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (selectedJfmcType) fetchVfcNames(selectedJfmcType._id);
  }, [selectedJfmcType]);

  useEffect(() => {
    const fetchLoanTypes = async () => {
      try {
        const config = Authorization();
        const response = await axios.get(`${Api}/api/loan-types`, config);
        setLoanTypes(response.data.loanTypes || []);
      } catch (error) {
        console.error("Error fetching loan types", error);
      }
    };
    fetchLoanTypes();
  }, [Api]);

  const handleEditClick = (repaymentId) => {
    setSelectedRepaymentId(repaymentId);
    setEditModalShow(true);
  };

  const fetchLoanCollections = async () => {
    if (!selectedVfc) { setError("Please select a VFC."); setLoanData([]); return; }
    setLoading(true);
    try {
      const config = Authorization();
      const response = await axios.post(
        `${Api}/api/loan-collections/get-loancollection-by-vfc-date`,
        { vfcId: selectedVfc, date: selectedDate, loanTypeId: selectedLoanType || null },
        config
      );
      const filteredLoanCollections = response.data.data;
      if (filteredLoanCollections.length > 0) { setLoanData(filteredLoanCollections); setError(null); }
      else { setLoanData([]); setError("No loan repayment found for the selected criteria."); }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
    await logActivity(userId, "Viewed Loan Collection", "Manage Loan Repayment Page", Api);
  };

  const handleDeleteRepayment = async (repaymentId) => {
    if (!repaymentId) return;
    if (!window.confirm("Are you sure you want to delete this loan repayment?")) return;
    setLoading(true);
    try {
      const config = Authorization();
      await axios.delete(`${Api}/api/loan-collections/${repaymentId}`, config);
      setLoanData((prev) => prev.filter((item) => item._id !== repaymentId));
      setError(null);
      await logActivity(userId, "Deleted Loan Collection", "Manage Loan Repayment Page", Api);
    } catch (err) {
      console.error(err);
      setError("Failed to delete loan repayment.");
    }
    setLoading(false);
  };
  const columns = [
    {
      headerName: "Action",
      field: "action",
      cellRenderer: (params) => (
        <div style={{ display: "flex", gap: 8, alignItems: "center", paddingTop: 4 }}>
          {permissions.includes("EditVfc") && (
            <i
              className="mdi mdi-pen"
              title="Edit"
              style={{
                fontSize: "1.2rem",
                cursor: "pointer",
                color: "#1565C0",
              }}
              onClick={() => handleEditClick(params.data._id)}
            />
          )}

          {permissions.includes("DeleteLoan") && (
            <i
              className="mdi mdi-delete"
              title="Delete"
              style={{
                fontSize: "1.2rem",
                cursor: "pointer",
                color: "#C62828",
              }}
              onClick={() => handleDeleteRepayment(params.data._id)}
            />
          )}
        </div>
      ),
    },

    {
      headerName: "SI.No",
      field: "index",
      valueGetter: (params) => params.node.rowIndex + 1,
    },

    {
      headerName: "Member Name",
      field: "loanId.members",
      valueGetter: (params) => {
        const members = params.data.loanId?.members;
        if (!members || members.length === 0) {
          return "N/A";
        }
        const names = members
          .map(member => member.name)
          .filter(Boolean);
        return names.length > 0 ? names.join(", ") : "N/A";
      },
    },
    // Show SHG Name only when at least one record has an SHG name
    ...(loanData.some((item) => item.loanId?.shgId?.name?.trim())
      ? [
        {
          headerName: "SHG Name",
          field: "loanId.shgId.name",
          valueGetter: (params) =>
            params.data?.loanId?.shgId?.name || "",
        },
      ]
      : []),
    {
      headerName: "Loan No",
      field: "loanId.loanNo",
      valueGetter: (params) =>
        params.data.loanId?.loanNo || "N/A",
    },

    {
      headerName: "Loan Issue Date",
      field: "loanId.date",
      valueFormatter: (params) =>
        params.value ? formatToLocalTime(params.value) : "N/A",
    },

    {
      headerName: "Loan Type",
      field: "loanTypeName",
      minWidth: 160,
      valueGetter: (params) => {
        return params.data?.loanType?.loanName ?? "N/A";
      },
    },
    {
      headerName: "JFMC Name",
      field: "jfmcName",
      minWidth: 180,
      valueGetter: (params) => {
        return params.data?.vfcId?.vfcName ?? "N/A";
      },
    },
    {
      headerName: "Amount",
      field: "amountPaid",
      cellRenderer: (params) => (
        <span
          style={{
            background: "#E8F5E9",
            color: "#2E7D32",
            fontSize: 11,
            fontWeight: 700,
            padding: "2px 8px",
            borderRadius: 50,
          }}
        >
          ₹{params.value || 0}
        </span>
      ),
    },

    {
      headerName: "Interest",
      field: "interestPaid",
      cellRenderer: (params) => (
        <span
          style={{
            background: "#E8F5E9",
            color: "#2E7D32",
            fontSize: 11,
            fontWeight: 700,
            padding: "2px 8px",
            borderRadius: 50,
          }}
        >
          ₹{params.value || 0}
        </span>
      ),
    },

    {
      headerName: "Payment Date",
      field: "paymentDate",
      valueFormatter: (params) =>
        params.value ? formatToLocalTime(params.value) : "N/A",
    },
  ];

  return (
    <div>
      {loading && <Preloader loading={loading} />}

      {/* ── Page Header ── */}
      <div style={{ marginBottom: 20 }}>
        <h4 style={{ margin: 0, fontWeight: 700, color: "#1A202C", fontSize: 18 }}>Manage Loan Repayments</h4>
        <div style={{ fontSize: 12, color: "#718096", marginTop: 3 }}>
          <span style={{ color: "#A0AEC0" }}>Loan</span>
          <span style={{ margin: "0 6px", color: "#CBD5E0" }}>›</span>
          <span style={{ color: "#2E7D32", fontWeight: 600 }}>Manage Repayments</span>
        </div>
      </div>

      {!selectedJfmcType ? (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #FED7D7", padding: "48px 24px", textAlign: "center" }}>
          <div style={{ width: 60, height: 60, background: "#FFF5F5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
            <i className="mdi mdi-tag-outline" style={{ fontSize: 28, color: "#E53E3E" }} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>JFMC Type not selected</div>
          <div style={{ fontSize: 12, color: "#A0AEC0" }}>Use the <strong>JFMC Type</strong> selector in the top navigation bar</div>
        </div>
      ) : (
        <>
          {/* ── Filter Card ── */}
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", padding: "20px 24px", marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <span style={{ width: 30, height: 30, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14 }}>
                <i className="mdi mdi-filter-variant" />
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>Filter Repayments</span>
            </div>

            {error && (
              <div style={{ marginBottom: 14, padding: "8px 14px", background: "#FFEBEE", border: "1px solid #FFCDD2", borderRadius: 8, fontSize: 13, color: "#C62828", display: "flex", alignItems: "center", gap: 8 }}>
                <i className="mdi mdi-alert-circle-outline" /> {error}
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 16, marginBottom: 16 }}>
              <div>
                  <label style={labelStyle}>Select JFMC</label>
                <select value={selectedVfc} onChange={(e) => setSelectedVfc(e.target.value)} style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}>
                    <option value="">— Select JFMC —</option>
                  {vfcOption.map((vfc) => <option key={vfc._id} value={vfc._id}>{vfc.vfcName}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Select Loan Type</label>
                <select value={selectedLoanType} onChange={(e) => setSelectedLoanType(e.target.value)} style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}>
                  <option value="">All Loan Types</option>
                  {loanTypes.map((lt) => <option key={lt._id} value={lt._id}>{lt.loanName}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Select Date</label>
                <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")} />
              </div>
              <div style={{ display: "flex", alignItems: "flex-end" }}>
                <button onClick={fetchLoanCollections}
                  style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 20px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 3px 10px rgba(46,125,50,0.3)" }}>
                  <i className="mdi mdi-magnify" /> Search
                </button>
              </div>
            </div>
          </div>

          {/* ── Table Card ── */}
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
              <span style={{ width: 30, height: 30, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15 }}>
                <i className="mdi mdi-cash-refund" />
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#fff", flex: 1 }}>Repayment Records</span>
              <span style={{ background: "rgba(255,255,255,0.2)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 50 }}>{loanData.length} records</span>
            </div>
            <div style={{ overflowX: "auto" }}>
              <div style={{ padding: "16px 20px" }}>
                <TableComponent data={loanData} columns={columns} />
              </div>
            </div>
          </div>

          <EditLoanRepayment
            show={editModalShow}
            onHide={() => setEditModalShow(false)}
            repaymentId={selectedRepaymentId}
            onUpdated={() => { setEditModalShow(false); }}
          />
        </>
      )}
    </div>
  );
};

export default ManageLoanRepayment;
